import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkoutSession } from '../contexts/WorkoutSessionContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  TrendingUp,
  Camera,
  BarChart2,
  Zap,
  MessageCircle,
  Star,
  Award,
  Apple,
  Activity,
  Users,
  User,
  LogOut,
  ChevronRight,
  Swords,
  Target,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useFeatureFlags, type FeatureFlagKey } from '../hooks/useFeatureFlag';

type SheetEntry = { path: string; icon: typeof Home; label: string; flag?: FeatureFlagKey };

// v3.1 tab bar: 5 primary tabs (Home · Training · Analysis · Skills · More).
// Progress moved to the sheet per user decision 4B — secondary weight, scroll-heavy.
const PRIMARY_TABS: SheetEntry[] = [
  { path: '/app', icon: Home, label: 'Home' },
  { path: '/training', icon: TrendingUp, label: 'Training' },
  { path: '/analysis', icon: Camera, label: 'Analysis' },
  { path: '/skills', icon: Star, label: 'Skills' },
];

const SHEET_NAV: SheetEntry[] = [
  { path: '/chat', icon: MessageCircle, label: 'Chat' },
  { path: '/progress', icon: BarChart2, label: 'Progress' },
  { path: '/recovery', icon: Zap, label: 'Recovery' },
  { path: '/nutrition', icon: Apple, label: 'Nutrition' },
  { path: '/duels', icon: Swords, label: 'Duels', flag: 'duels' },
  { path: '/challenge', icon: Target, label: 'Challenge', flag: 'challenge' },
  { path: '/hall-of-fame', icon: Award, label: 'Hall of Fame', flag: 'hallOfFame' },
  { path: '/injury-log', icon: Activity, label: 'Injury Log' },
  { path: '/community', icon: Users, label: 'Community', flag: 'community' },
];

const SHEET_ACCOUNT = [
  { path: '/profile', icon: User, label: 'Profile' },
];

export default function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isSessionActive, isAnalyzing } = useWorkoutSession();
  const { flags } = useFeatureFlags();
  const visibleSheetNav = SHEET_NAV.filter((item) => !item.flag || flags[item.flag]);
  if (isSessionActive || isAnalyzing) return null;

  const handleNavItem = (path: string) => {
    setSheetOpen(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    setSheetOpen(false);
    await supabase.auth.signOut();
    navigate('/login');
  };

  const moreActive = sheetOpen || visibleSheetNav.some((item) => location.pathname === item.path) || SHEET_ACCOUNT.some((item) => location.pathname === item.path);

  // Portal target: document.body. The nav uses position:fixed, but
  // PageTransition wraps every route in a transformed motion.div, and
  // any transformed ancestor breaks fixed positioning (CSS spec — a
  // transformed element becomes the containing block for fixed
  // descendants). Rendering through a portal moves the DOM outside
  // that chain so position:fixed pins to the viewport as intended.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Bottom Tab Bar */}
      <nav
        className="mobile-nav"
        style={{
          display: 'none',
          position: 'fixed',
          // Float the bar in from the screen edges instead of welding it
          // to the bottom. iOS Liquid-Glass aesthetic — feels like the
          // bar is hovering over content rather than docked to it.
          bottom: 'calc(env(safe-area-inset-bottom) + 10px)',
          left: 12,
          right: 12,
          height: '60px',
          // Apple Liquid-Glass spec corrections (research 2026-05-01).
          // 40px blur was too frosted (legibility cliff); Apple's
          // floating-nav blur sits at 8-12px. saturate(180%) was over-
          // saturating; spec is 120-150%. Result: clearer translucent
          // pane that reads as glass rather than tinted plastic.
          background: 'color-mix(in srgb, var(--color-canvas) 22%, transparent)',
          backdropFilter: 'blur(12px) saturate(150%) brightness(1.06)',
          WebkitBackdropFilter: 'blur(12px) saturate(150%) brightness(1.06)',
          border: '1px solid color-mix(in srgb, var(--color-ink-1) 8%, transparent)',
          borderRadius: '28px',
          // Stronger inner top highlight (specular line, alpha 0.18) is
          // what makes the bar read as glass instead of a tinted overlay.
          // Without it, no matter how good the blur is, it looks plastic.
          boxShadow: '0 8px 24px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.18)',
          // will-change tells the browser to GPU-allocate for backdrop
          // recomputation; contain prevents repaint cascades.
          willChange: 'backdrop-filter',
          contain: 'layout style paint',
          zIndex: 100,
          overflow: 'hidden',
        }}
      >
        {PRIMARY_TABS.map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <button
              key={path}
              type="button"
              onClick={() => navigate(path)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 0',
                color: active ? 'var(--color-cinder)' : 'var(--color-text-secondary)',
                transition: 'color 0.15s ease',
                position: 'relative',
              }}
            >
              <Icon size={20} strokeWidth={active ? 2.5 : 1.8} />
              <span style={{ fontSize: '10px', fontWeight: active ? 700 : 400, letterSpacing: '0.04em' }}>
                {label}
              </span>
              {active && (
                <motion.div
                  layoutId="activeTab"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'var(--color-cinder)',
                    borderRadius: '1px',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          );
        })}

        {/* More tab */}
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 0',
            color: moreActive ? 'var(--color-cinder)' : 'var(--color-text-secondary)',
            transition: 'color 0.15s ease',
            position: 'relative',
          }}
        >
          <User size={20} strokeWidth={moreActive ? 2.5 : 1.8} />
          <span style={{ fontSize: '10px', fontWeight: moreActive ? 700 : 400, letterSpacing: '0.04em' }}>
            You
          </span>
          {moreActive && !sheetOpen && (
            <motion.div
              layoutId="activeTab"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'var(--color-cinder)',
                borderRadius: '1px',
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
        </button>
      </nav>

      {/* More Sheet */}
      <AnimatePresence>
        {sheetOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSheetOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 110,
              }}
            />

            {/* Sheet */}
            <motion.div
              key="sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'var(--color-surface-ephemeral)',
                borderRadius: '20px 20px 0 0',
                zIndex: 120,
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
            >
              {/* Drag handle */}
              <div style={{ display: 'flex', justifyContent: 'center', margin: '10px auto 4px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '4px',
                    background: 'var(--color-border-strong)',
                    borderRadius: '2px',
                  }}
                />
              </div>

              {/* Sheet header */}
              <div style={{ padding: '16px 20px 8px' }}>
                <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600 }}>
                  YOU
                </span>
              </div>

              {/* Group 0: Profile + account — surfaced first because the tab is now "You" */}
              {SHEET_ACCOUNT.map(({ path, icon: Icon, label }) => {
                const active = location.pathname === path;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => handleNavItem(path)}
                    style={{
                      width: '100%',
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 20px',
                      gap: '14px',
                      background: 'none',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon size={18} color={active ? 'var(--color-cinder)' : 'var(--color-text-secondary)'} strokeWidth={1.8} />
                    <span style={{ flex: 1, fontSize: '15px', textAlign: 'left', color: active ? 'var(--color-cinder)' : 'var(--color-text-primary)', fontWeight: 500 }}>
                      {label}
                    </span>
                    <ChevronRight size={14} color="var(--color-ink-4)" />
                  </button>
                );
              })}

              {/* Spacer */}
              <div style={{ height: '16px' }} />

              {/* Group 1: first up-to-4 visible items */}
              {visibleSheetNav.slice(0, 4).map(({ path, icon: Icon, label }) => {
                const active = location.pathname === path;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => handleNavItem(path)}
                    style={{
                      width: '100%',
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 20px',
                      gap: '14px',
                      background: 'none',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon size={18} color={active ? 'var(--color-cinder)' : 'var(--color-text-secondary)'} strokeWidth={1.8} />
                    <span style={{ flex: 1, fontSize: '15px', textAlign: 'left', color: active ? 'var(--color-cinder)' : 'var(--color-text-primary)', fontWeight: 500 }}>
                      {label}
                    </span>
                    <ChevronRight size={14} color="var(--color-ink-4)" />
                  </button>
                );
              })}

              {/* Spacer between Group 1 and Group 2 */}
              <div style={{ height: '16px' }} />

              {/* Group 2: remaining visible items */}
              {visibleSheetNav.slice(4).map(({ path, icon: Icon, label }) => {
                const active = location.pathname === path;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => handleNavItem(path)}
                    style={{
                      width: '100%',
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 20px',
                      gap: '14px',
                      background: 'none',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon size={18} color={active ? 'var(--color-cinder)' : 'var(--color-text-secondary)'} strokeWidth={1.8} />
                    <span style={{ flex: 1, fontSize: '15px', textAlign: 'left', color: active ? 'var(--color-cinder)' : 'var(--color-text-primary)', fontWeight: 500 }}>
                      {label}
                    </span>
                    <ChevronRight size={14} color="var(--color-ink-4)" />
                  </button>
                );
              })}

              {/* Spacer before Sign out */}
              <div style={{ height: '16px' }} />

              {/* Sign out */}
              <button
                type="button"
                onClick={() => void handleSignOut()}
                style={{
                  width: '100%',
                  height: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  gap: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginBottom: '8px',
                }}
              >
                <LogOut size={18} color="#EF4444" strokeWidth={1.8} />
                <span style={{ flex: 1, fontSize: '15px', textAlign: 'left', color: '#EF4444', fontWeight: 500 }}>
                  Sign out
                </span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
}
